import { BookOpen, Loader2, Users } from 'lucide-react';
import type { WikilinkSuggestion } from '@/lib/wikilinkAutocomplete';
import { cn } from '@/lib/utils';

interface WikilinkSuggestionPopupProps {
  position: { top: number; left: number };
  suggestions: WikilinkSuggestion[];
  activeIndex: number;
  isLoading: boolean;
  query: string;
  onHover: (index: number) => void;
  onSelect: (index: number) => void;
}

/**
 * Floating dropdown shown while typing an in-progress `[[wikilink`,
 * positioned at the caret via absolute `top`/`left` pixel coordinates
 * (computed by `useWikilinkAutocomplete` using the mirror-div technique in
 * `@/lib/textareaCaret`).
 *
 * This is a plain absolutely-positioned `<div>` rather than a Radix
 * Popover/Command, because those anchor to a *trigger element* — there is
 * no such element here, just a caret position inside a `<textarea>` that
 * moves on every keystroke. Mouse interaction uses `onMouseDown` (not
 * `onClick`) so the textarea's `blur` handler doesn't fire first and close
 * the popup before the selection registers.
 */
export function WikilinkSuggestionPopup({
  position,
  suggestions,
  activeIndex,
  isLoading,
  query,
  onHover,
  onSelect,
}: WikilinkSuggestionPopupProps) {
  return (
    <div
      className="absolute z-50 w-72 overflow-hidden rounded-md border bg-popover text-popover-foreground shadow-md"
      style={{ top: position.top, left: position.left }}
      role="listbox"
      aria-label="Wiki article suggestions"
    >
      {isLoading && suggestions.length === 0 ? (
        <div className="flex items-center gap-2 px-3 py-2.5 text-sm text-muted-foreground">
          <Loader2 className="size-3.5 animate-spin" />
          Searching articles…
        </div>
      ) : suggestions.length === 0 ? (
        <div className="px-3 py-2.5 text-sm text-muted-foreground">
          No matching articles{query ? ` for "${query}"` : ''}. Keep typing to link to a new one.
        </div>
      ) : (
        <ul className="max-h-64 overflow-y-auto p-1">
          {suggestions.map((s, i) => (
            <li key={s.slug}>
              <button
                type="button"
                role="option"
                aria-selected={i === activeIndex}
                className={cn(
                  'flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-left text-sm',
                  i === activeIndex ? 'bg-accent text-accent-foreground' : 'hover:bg-accent/60',
                )}
                onMouseEnter={() => onHover(i)}
                onMouseDown={(e) => {
                  // Prevent the textarea from blurring before onSelect runs.
                  e.preventDefault();
                  onSelect(i);
                }}
              >
                <BookOpen className="size-4 shrink-0 text-muted-foreground" />
                <span className="flex-1 truncate">{s.title}</span>
                {s.versionCount > 1 && (
                  <span className="flex shrink-0 items-center gap-0.5 text-xs text-muted-foreground">
                    <Users className="size-3" />
                    {s.versionCount}
                  </span>
                )}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
